import {
  IssueAmountStructure,
  IssueCaseCategory,
  IssueCaseDoc,
  IssueCaseIssuedByType,
  IssueCaseRequestCategory,
  IssueCaseStatusCode,
  IssueTaskCategory,
} from '@gooduncles/gu-app-schema';
import { IssueCaseAnyCategory } from 'src/constants/issue.constants';
import {
  CellStyle,
  CellValueChangedEvent,
  ColGroupDef,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { message } from 'antd';
import { formatDate } from 'date-fns';

import { updateIssueCase } from 'src/utils/firebase/firebase-short-cut';

import { getCategoryLabel, getSubcategoryLabel } from './issue-data-util';
import { issueCaseIssuedByMap, issueCaseStatusBadgeMap } from './issue-util';

/**
 * 케이스 카테고리별 색상 매핑 (caseKind='issue' / 'request' 양쪽)
 */
export const categoryColorMap: Record<IssueCaseAnyCategory, string> = {
  [IssueCaseCategory.QUALITY]: '#ff4d4f', // 빨강
  [IssueCaseCategory.DELIVERY]: '#1890ff', // 파랑
  [IssueCaseCategory.SUPPLIER]: '#52c41a', // 초록
  [IssueCaseCategory.ORDER]: '#faad14', // 노랑/주황
  [IssueCaseRequestCategory.REQUEST_PRODUCT]: '#0f766e',
  [IssueCaseRequestCategory.REQUEST_ACCOUNT]: '#9333ea',
  [IssueCaseRequestCategory.REQUEST_DELIVERY]: '#0284c7',
  [IssueCaseRequestCategory.REQUEST_PAYMENT]: '#ca8a04',
  [IssueCaseRequestCategory.OTHER]: '#64748b',
};

/**
 * 태스크 카테고리별 색상 매핑
 */
export const taskCategoryColorMap: Record<IssueTaskCategory, string> = {
  [IssueTaskCategory.PICK_UP]: '#13c2c2', // 청록
  [IssueTaskCategory.DELIVERY]: '#1890ff', // 파랑
  [IssueTaskCategory.QUICK]: '#fa8c16', // 주황
  [IssueTaskCategory.REFUND]: '#eb2f96', // 분홍
  [IssueTaskCategory.ADDITIONAL_CHARGE]: '#722ed1', // 보라
  [IssueTaskCategory.OTHER]: '#8c8c8c', // 회색
  [IssueTaskCategory.INTERNAL]: '#595959', // 진한 회색
  [IssueTaskCategory.REQUEST]: '#0f766e', // 요청 응대
};

/**
 * 태스크 카테고리 셀 스타일
 */
export const taskCategoryCellStyle = <T extends { category?: IssueTaskCategory }>(params: {
  data?: T;
}): CellStyle | undefined => {
  if (!params.data?.category) return undefined;
  const color = taskCategoryColorMap[params.data.category];
  return color ? { color, fontWeight: 500 } : undefined;
};

export const issueCaseStatusValueFormatter = (params: ValueFormatterParams<IssueCaseDoc, IssueCaseStatusCode>) => {
  if (!params.value) return null;
  if (!issueCaseStatusBadgeMap[params.value]) return params.value;
  return issueCaseStatusBadgeMap[params.value].text;
};

/**
 * 상품 목록 필터 파라미터
 * - 각 상품명을 개별 필터 옵션으로 제공
 */
export const productsFilterParams = {
  keyCreator: (params: any) => {
    const products = params.data?.products;
    if (!products || products.length === 0) return [];
    return products.map((p: any) => p.productName);
  },
  convertValuesToStrings: true,
};

/**
 * 금액을 포맷팅하는 함수
 */
export const formatAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(amount);
};

/**
 * 등록일시 포맷터
 */
export const registeredAtValueFormatter = (params: ValueFormatterParams) => {
  return params.value ? formatDate(new Date(params.value), 'MM.dd HH:mm') : '-';
};

/**
 * 매장명 툴팁 getter - storeId 표시
 */
export const storeNameTooltipValueGetter = (params: ITooltipParams) => {
  return params.data?.storeId || '';
};

/**
 * 카테고리 valueFormatter - 한글 변환
 */
export const categoryValueFormatter = (params: ValueFormatterParams<IssueCaseDoc, IssueCaseCategory>) => {
  if (!params.value) return '-';
  return getCategoryLabel(params.value);
};

/**
 * 카테고리 cellStyle - 색상 적용
 */
export const categoryCellStyle = (params: any) => {
  if (!params.data?.category) return undefined;
  const color = categoryColorMap[params.data.category as IssueCaseCategory];
  return color ? { color, fontWeight: 500 } : undefined;
};

/**
 * 카테고리 필터 valueFormatter
 */
export const categoryFilterValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return null;
  return getCategoryLabel(params.value);
};

/**
 * 서브카테고리 valueGetter - 한글 변환
 */
export const subcategoryValueGetter = (params: ValueGetterParams) => {
  if (!params.data?.subcategory || !params.data?.category) return '-';
  return getSubcategoryLabel(params.data.category, params.data.subcategory);
};

/**
 * displayCategory valueGetter - 분류 / 세부분류 형태로 표시
 */
export const displayCategoryValueGetter = (params: ValueGetterParams) => {
  if (!params.data?.category) return '-';
  const baseCategory = getCategoryLabel(params.data.category);
  if (!params.data.subcategory) return baseCategory;
  const subCatStr = getSubcategoryLabel(params.data.category, params.data.subcategory);
  return subCatStr ? `${baseCategory} / ${subCatStr}` : baseCategory;
};

/**
 * 태스크 개수 valueGetter
 */
export const taskCountValueGetter = (params: ValueGetterParams) => {
  return params.data?.issueTasks?.length || 0;
};

/**
 * 내용 valueGetter - publicNote 또는 privateNote
 */
export const noteValueGetter = (params: ValueGetterParams) => {
  return params.data?.publicNote || params.data?.privateNote || '-';
};

/**
 * 내용 tooltipGetter - publicNote 또는 privateNote
 */
export const noteTooltipValueGetter = (params: ITooltipParams) => {
  return params.data?.publicNote || params.data?.privateNote || '';
};

/**
 * 금액 cellStyle - 음수일 때만 빨강
 */
export const amountCellStyle = (params: any): CellStyle => {
  const value = params.value;
  if (value === null || value === undefined) return { textAlign: 'right' };
  if (value < 0) return { textAlign: 'right', color: '#ff4d4f', fontWeight: 500 };
  return { textAlign: 'right' };
};

/**
 * Task 파트너명 valueGetter (배송 또는 픽업)
 */
export const taskPartnerNameValueGetter = (params: ValueGetterParams) => {
  const deliveryInfo = params.data?.deliveryInfo;
  const pickupInfo = params.data?.pickupInfo;

  if (deliveryInfo?.deliveryPartnerName) return deliveryInfo.deliveryPartnerName;
  if (pickupInfo?.pickupPartnerName) return pickupInfo.pickupPartnerName;
  return '-';
};

/**
 * Task 완료 시각 valueGetter (배송 또는 픽업)
 */
export const taskFinishedAtValueGetter = (params: ValueGetterParams) => {
  const deliveryInfo = params.data?.deliveryInfo;
  const pickupInfo = params.data?.pickupInfo;

  const finishedAt = deliveryInfo?.deliveryTaskFinishedAt || pickupInfo?.pickupTaskFinishedAt;
  return finishedAt || '-';
};

/**
 * Task 완료 시각 valueFormatter
 */
export const taskFinishedAtValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value || params.value === '-') return '-';
  return formatDate(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
};

/**
 * Task 대상 매장 valueGetter
 */
export const taskTargetStoreValueGetter = (params: ValueGetterParams) => {
  const deliveryInfo = params.data?.deliveryInfo;
  const pickupInfo = params.data?.pickupInfo;

  const storeName = deliveryInfo?.storeName || pickupInfo?.storeName;
  return storeName || '-';
};

/**
 * Task 메모 valueGetter
 */
export const taskMemoValueGetter = (params: ValueGetterParams) => {
  const deliveryInfo = params.data?.deliveryInfo;
  const pickupInfo = params.data?.pickupInfo;

  const memo = deliveryInfo?.memo || pickupInfo?.memo;
  return memo || '-';
};

/**
 * 외부 서비스명 매핑
 */
const externalServiceNameMap: Record<string, string> = {
  dver: '퀵(dver)',
  etc: '기타',
};

/**
 * Task 외부 서비스명 valueGetter
 */
export const externalServiceNameValueGetter = (params: ValueGetterParams) => {
  const externalService = params.data?.externalService;
  if (!externalService?.service) return '-';
  return externalServiceNameMap[externalService.service] || externalService.service;
};

/**
 * Task 외부 서비스 비용 valueGetter
 */
export const externalServiceCostValueGetter = (params: ValueGetterParams) => {
  const externalService = params.data?.externalService;
  return externalService?.cost ?? null;
};

/**
 * 특정 필드가 없을 때 회색으로 표시하는 cellStyle을 생성하는 팩토리 함수
 * @param fieldNames - 확인할 필드 이름(들). 배열인 경우 하나라도 있으면 활성화
 */
export const createDisabledCellStyle =
  (fieldNames: string | string[]) =>
  (params: any): CellStyle => {
    const fields = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    const hasValue = fields.some((field) => params.data?.[field] !== null);
    if (!hasValue) {
      return { color: '#d9d9d9', backgroundColor: '#f5f5f5' };
    }
    return {};
  };

/**
 * 파트너 정보가 없을 때 회색으로 표시하는 cellStyle
 */
export const partnerCellStyle = createDisabledCellStyle(['deliveryInfo', 'pickupInfo']);

/**
 * 외부 서비스가 없을 때 회색으로 표시하는 cellStyle
 */
export const externalServiceCellStyle = createDisabledCellStyle('externalService');

/**
 * 이슈 출처 valueFormatter - 한글 변환
 */
export const issuedByValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return '-';
  return issueCaseIssuedByMap[params.value as IssueCaseIssuedByType] || params.value;
};

/**
 * issuedBy 셀 에디터 파라미터
 */
export const issuedByCellEditorParams = {
  values: Object.keys(issueCaseIssuedByMap),
  formatValue: (value: IssueCaseIssuedByType) => issueCaseIssuedByMap[value] ?? value,
};

/**
 * issuedBy 셀 값 변경 핸들러
 * ag-grid의 onCellValueChanged에서 사용
 */
export const handleIssuedByCellValueChanged = (event: CellValueChangedEvent) => {
  const { data, newValue, oldValue } = event;
  if (!data || newValue === oldValue) return;

  updateIssueCase(data._id, { issuedBy: newValue as IssueCaseIssuedByType })
    .then(() => {
      message.success('이슈 출처가 변경되었습니다.');
    })
    .catch((error) => {
      console.error('Failed to update issuedBy:', error);
      message.error('이슈 출처 변경에 실패했습니다.');
      // 실패 시 원래 값으로 복원
      event.node.setDataValue('issuedBy', oldValue);
    });
};

/**
 * 금액 컬럼 그룹 정의를 생성하는 팩토리 함수
 * Issue 테이블들에서 공통으로 사용되는 금액(총액/공급가/세금) 컬럼 그룹
 */
export const createAmountColumnGroup = <T extends { amount?: IssueAmountStructure | null }>(): ColGroupDef<T> => ({
  headerName: '금액',
  children: [
    {
      field: 'amount.totalAmount',
      headerName: '총액',
      width: 120,
      valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
      cellStyle: amountCellStyle,
    },
    {
      field: 'amount.supplyAmount',
      headerName: '공급가',
      width: 120,
      columnGroupShow: 'open',
      valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'amount.taxAmount',
      headerName: '세금',
      width: 100,
      columnGroupShow: 'open',
      valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
      cellStyle: { textAlign: 'right' },
    },
  ],
});
